import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  TrendingUp,
  Activity,
  ArrowUpRight,
  TrendingDown,
  Clock,
  Sparkles
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DemoSidebar from '../components/DemoSidebar';
import DemoTopBar from '../components/DemoTopBar';
import { useNavigate } from 'react-router-dom';

const getDynamicFontSizeStyle = (val: string, baseSizeMobile = 20, baseSizeDesktop = 30) => {
  if (!val || typeof val !== 'string') return {};
  const len = val.length;
  if (len <= 7) return {};
  const reduction = (len - 7) * 0.85;
  const sizeMobile = Math.max(12, baseSizeMobile - reduction);
  const sizeDesktop = Math.max(14, baseSizeDesktop - reduction);
  return {
    fontSize: `clamp(${sizeMobile}px, 2vw, ${sizeDesktop}px)`
  };
};

const PreviewDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [hoveredRadar, setHoveredRadar] = useState<{ x: number; y: number; label: string; value: string } | null>(null);
  const [hoveredEquityPoint, setHoveredEquityPoint] = useState<any | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(120);
  const [cexName, setCexName] = useState<string>('Exchange');
  const [accountLabel, setAccountLabel] = useState<string>('Live Sync Preview');

  useEffect(() => {
    const rawPreview = localStorage.getItem('cc_preview_api');
    if (rawPreview) {
      try {
        const parsed = JSON.parse(rawPreview);
        setCexName(parsed.cex_name || 'Exchange');
        if (parsed.account_label) {
          setAccountLabel(parsed.account_label);
        }
      } catch (err) {
        // Safe fallback
      }
    }

    let startTimeStr = sessionStorage.getItem('cc_preview_start_time');
    if (!startTimeStr) {
      startTimeStr = Date.now().toString();
      sessionStorage.setItem('cc_preview_start_time', startTimeStr);
    }
    
    const startTime = parseInt(startTimeStr);

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, 120 - elapsed);
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        sessionStorage.removeItem('cc_preview_start_time');
        navigate('/register?from_preview=true');
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [navigate]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  const mockPortfolioData = React.useMemo(() => {
    const mockPerf = [
      { date: "01", realizedValue: 1240, positive: true, neutral: false, realized: "+$1,240", barLabel: "+$1,240", tradesCount: 4 },
      { date: "02", realizedValue: -420, positive: false, neutral: false, realized: "-$420", barLabel: "-$420", tradesCount: 2 },
      { date: "03", realizedValue: 0, positive: false, neutral: true, realized: "NO TRADES", barLabel: "NO TRADES", tradesCount: 0 },
      { date: "04", realizedValue: 890, positive: true, neutral: false, realized: "+$890", barLabel: "+$890", tradesCount: 7 },
      { date: "05", realizedValue: 2100, positive: true, neutral: false, realized: "+$2,100", barLabel: "+$2,100", tradesCount: 1 },
      { date: "07", realizedValue: -85, positive: false, neutral: false, realized: "-$85", barLabel: "-$85", tradesCount: 1 },
      { date: "09", realizedValue: 4200, positive: true, neutral: false, realized: "+$4,200", barLabel: "+$4,200", tradesCount: 3 },
      { date: "14", realizedValue: -85, positive: false, neutral: false, realized: "-$85", barLabel: "-$85", tradesCount: 0 }
    ];

    return {
      totalBalance: "$148,900.00",
      netPnl: "+$12,482",
      netPnlPercent: "14%",
      netPnlPositive: true,
      dayWinRate: "72.1%",
      dayWinRateSub: "31 / 43 DAYS",
      tradeWinRate: "68.4%",
      tradeWinRateSub: "84 / 123 TRADES",
      avgWinLoss: "+$420 / -$185",
      rrRatio: "RR: 2.27",
      performance: mockPerf,
      activeDeployments: [
        {
          id: 1,
          asset: "BTC / USDT",
          type: "LONG (20X)",
          entry: "$64,120.45",
          size: "0.45 BTC",
          pnl: "+$842.10",
          percent: "+1.2%",
          duration: "04:12:35",
          positive: true,
          exchange: cexName.toUpperCase()
        },
        {
          id: 2,
          asset: "ETH / USDT",
          type: "SHORT (10X)",
          entry: "$2,410.15",
          size: "12.50 ETH",
          pnl: "-$154.20",
          percent: "-0.6%",
          duration: "01:45:12",
          positive: false,
          exchange: cexName.toUpperCase()
        }
      ]
    };
  }, [cexName]);

  const displayPortfolioData = mockPortfolioData;

  const daysInMonth = 30;
  const startOffset = 2;

  const balance = displayPortfolioData.totalBalance;
  const pnl = displayPortfolioData.netPnl;
  const pnlPercent = displayPortfolioData.netPnlPercent;
  const pnlPositive = displayPortfolioData.netPnlPositive;

  const dayWinRate = displayPortfolioData.dayWinRate;
  const dayWinRateSub = displayPortfolioData.dayWinRateSub;
  const tradeWinRate = displayPortfolioData.tradeWinRate;
  const tradeWinRateSub = displayPortfolioData.tradeWinRateSub;
  const avgWinLoss = displayPortfolioData.avgWinLoss;
  const rrRatio = displayPortfolioData.rrRatio;

  const handleEquityMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pctX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

    const points = [10000, 20000, 45000, 35000, 68000, 80000, 122450, 95000, 130000, 148900, 148900];

    const maxB = 150000;
    const minB = 0;
    const range = maxB - minB;

    const pointsCount = points.length;
    const idx = Math.round(pctX * (pointsCount - 1));
    const clampedIdx = Math.max(0, Math.min(pointsCount - 1, idx));

    const x = (clampedIdx / (pointsCount - 1)) * 100;
    const y = 100 - ((points[clampedIdx] - minB) / range) * 100;

    const dates = ["START", "WED 01", "THU 02", "FRI 03", "SAT 04", "SUN 05", "MON 06", "TUE 07", "SUN 09", "MON 10", "TUE 11"];
    const dateStr = dates[clampedIdx] || "Sept 2024";
    const pnls = ["", "", "-$420.00", "NO TRADES", "+$890.00", "+$2,100.00", "", "-$85.00", "+$4,200.00", "", ""];
    const pnlStr = pnls[clampedIdx] || "";
    const positive = !pnlStr.includes("-");

    const fmtBal = points[clampedIdx].toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });

    setHoveredEquityPoint({
      x,
      y,
      balance: fmtBal,
      date: dateStr,
      pnl: pnlStr || undefined,
      positive
    });
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      <DemoSidebar />

      <main className="flex-1 bg-[#050505] min-h-screen pt-16 md:pt-0 overflow-x-hidden">
        <DemoTopBar title="Live Sync Preview" />

        <div className="mx-auto w-full max-w-[1132px] pt-4 md:pt-8 px-3 md:px-0">
          <div className="relative overflow-hidden bg-gradient-to-r from-red-950/40 to-neutral-900 border border-red-500/30 rounded-3xl p-4 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 shadow-[0_0_50px_rgba(239,68,68,0.1)]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 rounded-full blur-[80px] pointer-events-none" />
            
            <div className="flex items-center gap-4 relative z-10 font-sans">
              <div className="w-12 h-12 rounded-2xl bg-red-600/20 border border-red-500/30 flex items-center justify-center text-red-500 shrink-0 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
                <Clock size={22} className="animate-pulse" />
              </div>
              <div>
                <h4 className="text-sm font-bold tracking-wide text-white">Live Connection Preview Active</h4>
                <p className="text-xs text-neutral-400 mt-1 max-w-xl">
                  You are previewing live synced data for <span className="text-white font-extrabold capitalize">{cexName}</span> ({accountLabel}). Complete your account registration to keep this CEX integration permanently.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2 relative z-10 shrink-0">
              <div className="flex items-center gap-2 bg-neutral-950/80 border border-neutral-800 rounded-xl px-4 py-2">
                <span className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Session Time Left:</span>
                <span className="text-xs font-black font-mono text-red-500">{formattedTime}</span>
              </div>
              <button 
                onClick={() => {
                  sessionStorage.removeItem('cc_preview_start_time');
                  navigate('/register?from_preview=true');
                }}
                className="w-full px-5 py-2.5 rounded-xl bg-[#E50914] hover:bg-[#E50914]/90 text-white text-xs font-bold transition-all shadow-lg shadow-red-950/20 hover:scale-105"
              >
                Register Account Now
              </button>
            </div>
          </div>
        </div>

        <div className="py-4 md:py-8 flex flex-col items-center w-full px-3 md:px-0 overflow-x-hidden">
          <div className="w-full max-w-[1132px] space-y-8">
            <header className="space-y-1 select-none">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-black tracking-tight">
                    {t('dashboard.title')}
                  </h1>
                  <p className="text-neutral-500 text-sm">
                    {accountLabel} · Live Connection Preview
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-red-950/20 border border-red-900/30 rounded-full px-4 py-2 select-none">
                  <div className="w-2 h-2 bg-[#E50914] rounded-full animate-pulse" />
                  <span className="text-xs font-bold text-[#E50914] uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles size={12} /> Sync Active
                  </span>
                </div>
              </div>
            </header>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              <StatCard
                label={t('dashboard.netPnl')}
                value={pnl}
                subValue={pnlPercent}
                positive={pnlPositive}
              />
              <StatCard
                label={t('dashboard.dayWinRate')}
                value={dayWinRate}
                subValue={dayWinRateSub}
              />
              <StatCard
                label={t('dashboard.tradeWinRate')}
                value={tradeWinRate}
                subValue={tradeWinRateSub}
              />
              <StatCard
                label={t('dashboard.avgWinLoss')}
                value={avgWinLoss}
                subValue={rrRatio}
                custom
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-6 font-sans">
              <div className="w-full lg:w-[356px] lg:flex-shrink-0">
                <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 relative overflow-hidden select-none flex flex-col justify-between min-h-[420px] lg:h-[502px] w-full gap-6">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#E50914]/[0.01] rounded-full blur-[80px] pointer-events-none" />
                  <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest mb-2">{t('dashboard.catatCryptoScore')}</h3>
                  
                  <div className="relative flex-1 flex items-center justify-center max-h-[260px] my-auto">
                    <svg className="w-full h-full max-w-[270px]" viewBox="0 0 270 220">
                      <circle cx="135" cy="110" r="95" fill="none" stroke="#222330" strokeWidth="1" />
                      <circle cx="135" cy="110" r="65" fill="none" stroke="#1C1E28" strokeWidth="1" strokeDasharray="3 3" />
                      <circle cx="135" cy="110" r="35" fill="none" stroke="#1C1E28" strokeWidth="1" strokeDasharray="3 3" />
                      <line x1="135" y1="15" x2="135" y2="205" stroke="#222330" strokeWidth="1" />
                      <line x1="40" y1="110" x2="230" y2="110" stroke="#222330" strokeWidth="1" />
                      
                      {(() => {
                        const riskScore = 95;
                        const volScore = 82;
                        const sharpeScore = 74;
                        const yieldScore = 88;

                        const scores = [riskScore / 100, volScore / 100, sharpeScore / 100, sharpeScore / 100, yieldScore / 100];
                        const maxR = 95;
                        const cx = 135, cy = 110;
                        const angles = [
                          -Math.PI / 2,
                          -Math.PI / 2 + (2 * Math.PI / 5),
                          -Math.PI / 2 + (4 * Math.PI / 5),
                          -Math.PI / 2 + (6 * Math.PI / 5),
                          -Math.PI / 2 + (8 * Math.PI / 5),
                        ];
                        
                        const vertices = scores.map((s, i) => {
                          const r = Math.max(0.05, s) * maxR;
                          const labelNames = ["Risk", "Vol", "Sharpe", "Sharpe", "Yield"];
                          const scoreVals = [riskScore, volScore, sharpeScore, sharpeScore, yieldScore];
                          return {
                            x: cx + r * Math.cos(angles[i]),
                            y: cy + r * Math.sin(angles[i]),
                            label: `${labelNames[i]} (${scoreVals[i]})`,
                            value: i === 2 || i === 3 
                              ? `${sharpeScore} Sharpe Ratio` 
                              : `${scoreVals[i]}% Score`
                          };
                        });

                        const pts = vertices.map(v => `${v.x},${v.y}`).join(' ');

                        return (
                          <g>
                            <polygon
                              points={pts}
                              fill="rgba(229, 9, 20, 0.15)"
                              stroke="#E50914"
                              strokeWidth="2"
                            />
                            {vertices.map((v, i) => (
                              <circle
                                key={i}
                                cx={v.x}
                                cy={v.y}
                                r="5"
                                fill="#E50914"
                                stroke="#FFFFFF"
                                strokeWidth="1.2"
                                className="cursor-pointer transition-colors duration-200 hover:fill-white hover:stroke-[#E50914]"
                                onMouseEnter={() => setHoveredRadar({ x: v.x, y: v.y, label: v.label, value: v.value })}
                                onMouseLeave={() => setHoveredRadar(null)}
                              />
                            ))}
                          </g>
                        );
                      })()}
                      <text x="135" y="10" textAnchor="middle" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">Risk (95)</text>
                      <text x="238" y="113" textAnchor="start" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">Vol (82)</text>
                      <text x="135" y="215" textAnchor="middle" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">Sharpe (74)</text>
                      <text x="32" y="113" textAnchor="end" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">Yield (88)</text>
                    </svg>
                    
                    {hoveredRadar && (
                      <div
                        className="absolute bg-neutral-950/95 border border-red-500/30 rounded-xl px-3 py-1.5 text-xs text-white pointer-events-none shadow-2xl backdrop-blur-md z-50 transition-all"
                        style={{
                          left: `${(hoveredRadar.x / 270) * 100}%`,
                          top: `${(hoveredRadar.y / 220) * 100}%`,
                          transform: 'translate(-50%, -125%)'
                        }}
                      >
                        <p className="font-bold text-neutral-400 text-[9px] uppercase tracking-wider leading-none">{hoveredRadar.label}</p>
                        <p className="text-xs font-extrabold text-[#E50914] mt-1 leading-none">{hoveredRadar.value}</p>
                      </div>
                    )}

                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
                      <span className="text-5xl font-black text-white tracking-tighter font-sans mt-3">92</span>
                      <span className="text-xs font-bold text-[#E50914] tracking-widest mt-1 uppercase select-none">A+ TIER</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="bg-[#141414] p-4 rounded-2xl text-center select-none">
                      <p className="text-[9px] text-[#777777] uppercase font-semibold tracking-widest mb-1.5 font-sans">{t('dashboard.riskRating')}</p>
                      <p className="text-sm font-extrabold text-white uppercase font-sans tracking-wide">{t('dashboard.excellent')}</p>
                    </div>
                    <div className="bg-[#141414] p-4 rounded-2xl text-center select-none">
                      <p className="text-[9px] text-[#777777] uppercase font-semibold tracking-widest mb-1.5 font-sans">{t('dashboard.consistency')}</p>
                      <p className="text-sm font-extrabold text-white uppercase font-sans tracking-wide">{t('dashboard.top5Percent')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full lg:flex-1 flex flex-col gap-6">
                <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 flex flex-col justify-between w-full min-h-[220px] lg:h-[263px] relative font-sans">
                  <div className="flex justify-between items-start mb-2 select-none">
                    <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">
                      {t('dashboard.cumulativeEquityCurve')}
                    </h3>
                    <div className="text-right">
                      <p className="text-[8px] text-neutral-500 uppercase font-semibold mb-1.5 tracking-wider -translate-y-[4px]">
                        Y-Axis: Total Balance (USD)
                      </p>
                      <p className="text-[11px] font-bold text-white tracking-tight font-sans" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        $148,900
                      </p>
                    </div>
                  </div>
                  
                  <div className="w-full h-[176px] flex relative mt-2 min-h-0">
                    <div className="flex flex-col justify-between items-start w-[40px] h-full flex-shrink-0 select-none text-left -translate-y-[4px]">
                      <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$150,000</span>
                      <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$100,000</span>
                      <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$50,000</span>
                      <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$0</span>
                    </div>
                    
                    <div 
                      className="relative flex-1 min-h-0"
                      onMouseMove={handleEquityMouseMove}
                      onMouseLeave={() => setHoveredEquityPoint(null)}
                    >
                      <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 664 176">
                        <linearGradient id="equityGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="rgba(34,197,94,0.45)" />
                          <stop offset="100%" stopColor="rgba(34,197,94,0.0)" />
                        </linearGradient>
                        <polygon points="0 176, 66 160, 132 110, 198 122, 264 80, 330 65, 396 15, 462 48, 528 8, 594 0, 664 0, 664 176" fill="url(#equityGradient)" />
                        <path d="M 0 176 L 66 160 L 132 110 L 198 122 L 264 80 L 330 65 L 396 15 L 462 48 L 528 8 L 594 0 L 664 0" fill="none" stroke="#22C55E" strokeWidth="2.5" />
                      </svg>

                      {hoveredEquityPoint && (
                        <>
                          <div 
                            className="absolute top-0 bottom-0 w-px bg-neutral-800/80 pointer-events-none"
                            style={{ left: `${hoveredEquityPoint.x}%` }}
                          />
                          <div 
                            className="absolute w-2 h-2 rounded-full bg-[#22C55E] border-2 border-white pointer-events-none z-30"
                            style={{ left: `${hoveredEquityPoint.x}%`, top: `${hoveredEquityPoint.y}%`, transform: 'translate(-50%, -50%)' }}
                          />
                          <div
                            className="absolute bg-neutral-950/95 border border-neutral-800 rounded-xl p-2.5 text-xs text-white pointer-events-none shadow-2xl backdrop-blur-md z-50 space-y-1"
                            style={{
                              left: `${hoveredEquityPoint.x}%`,
                              top: `${hoveredEquityPoint.y}%`,
                              transform: hoveredEquityPoint.x > 75 ? 'translate(-105%, -50%)' : 'translate(15%, -50%)'
                            }}
                          >
                            <p className="font-bold text-[9px] text-[#777777] uppercase tracking-wider leading-none">{hoveredEquityPoint.date}</p>
                            <p className="text-xs font-black text-white mt-1 leading-none">{hoveredEquityPoint.balance}</p>
                            {hoveredEquityPoint.pnl && (
                              <p className={`text-[10px] font-bold ${hoveredEquityPoint.positive ? 'text-[#22C55E]' : 'text-[#EF4444]'} mt-1 leading-none`}>
                                {hoveredEquityPoint.pnl}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 flex flex-col justify-between w-full min-h-[200px] lg:h-[215px] relative font-sans">
                  <div className="flex justify-between items-start mb-2 select-none">
                    <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">{t('dashboard.dailyRealizedPnL')}</h3>
                    <p className="text-[8px] text-neutral-500 uppercase font-semibold tracking-wider">{t('dashboard.last10Days')}</p>
                  </div>
                  
                  <div className="w-full h-32 flex relative mt-2 min-h-0">
                    <div className="flex flex-col justify-between items-start w-[40px] h-full flex-shrink-0 select-none text-left -translate-y-[4px]">
                      <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">+$5,000</span>
                      <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$0</span>
                      <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">-$5,000</span>
                    </div>
                    
                    <div className="relative flex-1 flex items-end justify-between gap-1.5 h-full px-2 min-h-0">
                      <div className="absolute top-[50%] left-0 right-0 h-px bg-neutral-800/80 z-0 pointer-events-none" />
                      {displayPortfolioData.performance.map((day, i) => {
                        const h = 40;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full relative group">
                            {day.realizedValue !== 0 && (
                              <div 
                                className={`w-full rounded-t-sm transition-all duration-300 relative z-10 ${
                                  day.positive 
                                    ? 'bg-gradient-to-t from-[#22C55E]/40 to-[#22C55E]' 
                                    : 'bg-gradient-to-b from-[#EF4444] to-[#EF4444]/40'
                                }`}
                                style={{ 
                                  height: `${h}%`,
                                  transform: day.positive ? 'translateY(-50%)' : 'translateY(50%)',
                                  transformOrigin: day.positive ? 'bottom' : 'top'
                                }}
                              />
                            )}
                            <span className="absolute bottom-0 text-[7px] font-bold text-neutral-600 font-sans select-none">{day.date}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 font-sans">
              <div className="lg:col-span-7 bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-4 md:p-6 flex flex-col justify-between h-[360px] md:h-[480px]">
                <div className="flex justify-between items-center mb-4 shrink-0 select-none">
                  <div>
                    <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">{t('dashboard.performanceCalendar')}</h3>
                    <p className="text-[11px] font-bold text-white tracking-tight font-sans mt-0.5">
                      September 2024
                    </p>
                  </div>
                </div>

                <div className="flex-1 grid grid-cols-7 grid-rows-5 gap-1.5 md:gap-2.5 min-h-0">
                  {Array.from({ length: startOffset }).map((_, idx) => (
                    <div key={`empty-${idx}`} className="bg-[#0A0A0A] p-1 md:p-4 h-[44px] md:h-[90px] opacity-20 border border-[#1C1D26]/20 rounded-lg"></div>
                  ))}

                  {Array.from({ length: daysInMonth }).map((_, idx) => {
                    const dayNum = idx + 1;
                    const dayData = displayPortfolioData.performance.find(d => parseInt(d.date) === dayNum);

                    if (dayData) {
                      return (
                        <div
                          key={dayNum}
                          className={`relative p-1 md:p-3 flex flex-col justify-between aspect-square md:aspect-auto h-auto rounded-xl border select-none transition-all duration-300 ${
                            dayData.tradesCount > 0 
                              ? dayData.positive 
                                ? 'bg-[#10B981]/5 border-[#10B981]/25 hover:border-[#10B981]/50' 
                                : 'bg-[#EF4444]/5 border-[#EF4444]/25 hover:border-[#EF4444]/50'
                              : 'bg-[#14151E]/40 border-[#1C1D26]/40 hover:border-neutral-800'
                          }`}
                        >
                          <div className="flex justify-between items-start w-full relative z-10">
                            <span className="text-[9px] md:text-xs font-black text-neutral-500 font-mono leading-none">{dayNum}</span>
                          </div>
                          
                          <div className="mt-auto w-full flex flex-col justify-end relative z-10">
                            {dayData.tradesCount > 0 ? (
                              <p className={`text-[8px] md:text-[12px] font-black tracking-tight font-sans leading-none mb-1 md:mb-2 ${
                                dayData.positive ? 'text-[#10B981]' : 'text-[#EF4444]'
                              }`}>
                                {dayData.realized}
                              </p>
                            ) : (
                              <p className="text-[10px] font-semibold tracking-tight font-sans text-neutral-800 leading-none">-</p>
                            )}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={dayNum}
                        className="relative p-1 md:p-3 flex flex-col justify-between aspect-square md:aspect-auto h-auto rounded-xl border border-[#1C1D26]/40 select-none bg-[#14151E]/40"
                      >
                        <span className="text-[9px] md:text-xs font-black text-neutral-500 font-mono leading-none">{dayNum}</span>
                        <p className="text-[10px] font-semibold tracking-tight font-sans text-neutral-800 leading-none mt-auto">-</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="lg:col-span-5 bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl overflow-hidden flex flex-col justify-between h-[360px] md:h-[480px]">
                <div className="p-6 pb-4 shrink-0 select-none">
                  <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">{t('dashboard.activeDeployments')}</h3>
                </div>

                <div className="flex-1 overflow-y-auto px-2 md:px-0">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-[#14151E] text-[9px] uppercase font-bold text-neutral-500 tracking-wider sticky top-0 z-20 border-b border-[#1C1D26]/40 select-none">
                      <tr>
                        <th className="px-8 py-3.5">Asset</th>
                        <th className="px-8 py-3.5">Entry</th>
                        <th className="px-8 py-3.5">Size</th>
                        <th className="px-8 py-3.5">Unrealized PnL</th>
                        <th className="px-8 py-3.5">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1C1D26]/40">
                      {displayPortfolioData.activeDeployments.map((dep: any) => (
                        <tr key={dep.id} className="hover:bg-neutral-800/10 transition-colors group cursor-pointer border-b border-[#1C1D26]/40 last:border-none">
                          <td className="px-8 py-5">
                            <div>
                              <p className="text-xs font-bold text-white mb-0.5 tracking-tight font-sans">{dep.asset}</p>
                              <span className={`text-[9px] font-extrabold tracking-wider font-sans ${dep.positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                {dep.type}
                              </span>
                            </div>
                          </td>
                          <td className="px-8 py-5 text-xs font-semibold text-neutral-300 font-sans tracking-wide">{dep.entry}</td>
                          <td className="px-8 py-5 text-xs font-semibold text-neutral-300 font-sans tracking-wide">{dep.size}</td>
                          <td className="px-8 py-5">
                            <p className={`text-xs font-bold ${dep.positive ? 'text-[#10B981]' : 'text-[#EF4444]'} mb-0.5 font-sans tracking-wide`}>{dep.pnl}</p>
                            <p className={`text-[9px] font-semibold ${dep.positive ? 'text-[#10B981]/80' : 'text-[#EF4444]/80'} tracking-widest font-sans`}>{dep.percent}</p>
                          </td>
                          <td className="px-8 py-5 text-xs font-semibold text-neutral-400 font-sans tracking-wide">{dep.duration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ label, value, subValue, positive = false, custom = false }: any) => {
  const isNeutral = subValue?.includes("0/") || value === "$0 / $0" || value === "0%";
  return (
    <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-4 md:p-6 select-none font-sans relative overflow-hidden flex flex-col justify-between h-[105px] md:h-[135px]">
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/[0.01] rounded-full blur-[40px] pointer-events-none" />
      <p className="text-[9px] md:text-[10px] uppercase font-semibold text-neutral-500 tracking-widest leading-none mb-1 md:mb-2">{label}</p>
      <div className="my-auto flex flex-col justify-center">
        <h2 className="text-lg md:text-2.5xl font-black text-white tracking-tight leading-tight" style={getDynamicFontSizeStyle(value, 18, 26)}>{value}</h2>
      </div>
      <div className="mt-auto flex items-center justify-between">
        {custom ? (
          <span className="text-[10px] md:text-xs font-bold text-neutral-400">{subValue}</span>
        ) : (
          <span className={`text-[10px] md:text-xs font-bold flex items-center gap-1 leading-none ${
            isNeutral ? 'text-neutral-500' : positive ? 'text-[#10B981]' : 'text-[#EF4444]'
          }`}>
            {!isNeutral && (positive ? <ArrowUpRight size={12} /> : <TrendingDown size={12} className="rotate-90" />)}
            {subValue}
          </span>
        )}
      </div>
    </div>
  );
};

export default PreviewDashboard;
